import { useState } from 'react';

interface WriteReviewModalProps {
  item: {
    id: string;
    name: string;
    image: string;
  };
  onClose: () => void;
  onSubmitted: () => void;
}

interface WriteForm {
  author: string;
  rating: number;
  title: string;
  body: string;
}

const STORAGE_KEY = 'user_reviews';

function StarSelector({ rating, onSelect }: { rating: number; onSelect: (r: number) => void }) {
  const [hovered, setHovered] = useState(0);
  const LABELS = ['', '매우 불만족', '불만족', '보통', '만족', '매우 만족'];

  return (
    <div>
      <div className="flex items-center gap-1 mb-1.5">
        {[1, 2, 3, 4, 5].map((star) => {
          const active = (hovered || rating) >= star;
          return (
            <button
              key={star}
              type="button"
              onClick={() => onSelect(star)}
              onMouseEnter={() => setHovered(star)}
              onMouseLeave={() => setHovered(0)}
              className="cursor-pointer transition-transform hover:scale-110"
              style={{ background: 'none', border: 'none', padding: '2px' }}
              aria-label={`별점 ${star}점`}
            >
              <i
                className={`${active ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-gray-300'} text-3xl w-8 h-8 flex items-center justify-center`}
              />
            </button>
          );
        })}
      </div>
      <p className="text-sm font-semibold text-emerald-600 h-5">
        {LABELS[hovered || rating]}
      </p>
    </div>
  );
}

export default function WriteReviewModal({ item, onClose, onSubmitted }: WriteReviewModalProps) {
  const [form, setForm] = useState<WriteForm>({ author: '', rating: 5, title: '', body: '' });
  const [done, setDone] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.author.trim() || !form.title.trim() || !form.body.trim()) return;

    const newReview = {
      id: `user-${Date.now()}`,
      productId: item.id,
      author: form.author.trim(),
      avatar: form.author.trim()[0] ?? '익',
      rating: form.rating,
      date: new Date().toISOString().split('T')[0],
      title: form.title.trim(),
      body: form.body.trim(),
      verified: true,
      helpful: 0,
    };

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const existing = raw ? JSON.parse(raw) : [];
      existing.unshift(newReview);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
    } catch { /* ignore */ }

    setDone(true);
    onSubmitted();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 animate-fadeIn"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-fadeIn max-h-[90vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
              <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" />
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">리뷰 작성</p>
              <p className="text-sm font-bold text-gray-900 leading-tight">{item.name}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer transition-colors flex-shrink-0"
          >
            <i className="ri-close-line text-gray-500 w-4 h-4 flex items-center justify-center" />
          </button>
        </div>

        {done ? (
          /* 완료 화면 */
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-checkbox-circle-fill text-emerald-500 text-3xl w-8 h-8 flex items-center justify-center" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-2">리뷰가 등록됐어요!</h3>
            <p className="text-sm text-gray-500 mb-6">소중한 리뷰 감사합니다. 제품 페이지에서 확인하실 수 있어요.</p>
            <button
              type="button"
              onClick={onClose}
              className="w-full py-3 bg-emerald-600 text-white rounded-full font-bold text-sm hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              확인
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 별점 선택 */}
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-2">
                별점 <span className="text-red-500">*</span>
              </label>
              <StarSelector rating={form.rating} onSelect={(r) => setForm((p) => ({ ...p, rating: r }))} />
            </div>

            {/* 닉네임 */}
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5">
                닉네임 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.author}
                onChange={(e) => setForm((p) => ({ ...p, author: e.target.value }))}
                placeholder="홍길동"
                maxLength={20}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
            </div>

            {/* 리뷰 제목 */}
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5">
                리뷰 제목 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="한 줄로 요약해주세요"
                maxLength={50}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
            </div>

            {/* 상세 내용 */}
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5">
                상세 리뷰 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={form.body}
                onChange={(e) => { if (e.target.value.length <= 500) setForm((p) => ({ ...p, body: e.target.value })); }}
                placeholder="제품 사용 경험을 자유롭게 작성해주세요."
                rows={4}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none"
              />
              <p className="text-xs text-gray-400 text-right mt-0.5">{form.body.length}/500</p>
            </div>

            {/* 구매 확인 뱃지 안내 */}
            <div className="flex items-center gap-2 bg-emerald-50 rounded-xl px-3 py-2.5">
              <i className="ri-shield-check-line text-emerald-500 w-4 h-4 flex items-center justify-center flex-shrink-0" />
              <p className="text-xs text-emerald-700">주문 내역에서 작성된 리뷰에는 <strong>구매 확인</strong> 뱃지가 자동으로 붙어요.</p>
            </div>

            {/* 버튼 */}
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 border-2 border-gray-200 text-gray-600 rounded-full text-sm font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={!form.author.trim() || !form.title.trim() || !form.body.trim()}
                className="flex-[2] py-3 bg-emerald-600 text-white rounded-full text-sm font-bold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <i className="ri-pencil-line w-4 h-4 flex items-center justify-center" />
                리뷰 등록하기
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
